import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import {
  BadgeCheck,
  Ban,
  CheckCircle2,
  ChevronDown,
  FileSpreadsheet,
  History,
  Info,
  Loader2,
  MoreHorizontal,
  Pencil,
  Plus,
  RefreshCw,
  RotateCcw,
  ShieldAlert,
  ShieldCheck,
  Trash2,
  X,
} from "lucide-react";
import { fetchOrgAuditLogs } from "../../services/auditService";
import "./bankPayment.css";
import { AuthContext } from "../Context/AuthContext";
import {
  resolvePayPeriodDisplay,
  resolvePayrollRunDisplay,
} from "../../components/common/displayUtils";
import PayrollRunSelect from "../../components/common/PayrollRunSelect";
import {
  BrixBtn,
  BrixPageHero,
  BrixPageShell,
} from "../../components/layout/BrixPageShell";
import {
  approveSalaryBatchAdmin,
  approveSalaryBatchHr,
  cancelSalaryBatch,
  createSalaryBatch,
  deleteSalaryBatch,
  fetchArtifactBlob,
  formatFinancialApiError,
  generateSalaryBatchBankFile,
  getBankFileOptions,
  listSalaryBatches,
  parseBankFileValidationError,
  payoutSalaryBatch,
  rejectSalaryBatch,
  reopenSalaryBatch,
  updateSalaryBatch,
  validateSalaryBatch,
} from "./disbursementApi";
import { resolveRunStatus } from "../payroll/payrollRunUtils";
import { downloadBlob } from "../Employees/employeeFileUtils";
import GenerateDisbursementDialog from "./GenerateDisbursementDialog";

const APPROVED_STATES = ["approved", "ready_for_bank", "bank_generated"];

function base64ToBlob(base64, mimeType) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
  return new Blob([bytes], { type: mimeType || "application/octet-stream" });
}

function mimeForBankExport(ext) {
  const e = (ext || "").toLowerCase();
  if (e === "xlsx" || e === "excel") {
    return "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
  }
  if (e === "pdf") return "application/pdf";
  if (e === "csv") return "text/csv";
  return "application/octet-stream";
}
const isApproved = (status) => APPROVED_STATES.includes((status || "").toLowerCase());

const EDITABLE_STATUSES = new Set([
  "draft",
  "hr_pending",
  "finance_pending",
  "approved",
  "ready_for_bank",
  "bank_generated",
  "rejected",
]);

const DELETABLE_STATUSES = new Set([
  "draft",
  "hr_pending",
  "finance_pending",
  "approved",
  "ready_for_bank",
  "bank_generated",
  "rejected",
  "cancelled",
]);

const statusPill = (status) => {
  const s = (status || "").toLowerCase();
  if (s === "paid" || s === "completed") return "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200";
  if (s === "failed" || s === "rejected") return "bg-rose-50 text-rose-700 ring-1 ring-rose-200";
  if (s === "hr_pending" || s === "draft" || s.includes("pending"))
    return "bg-amber-50 text-amber-800 ring-1 ring-amber-200";
  if (s === "approved" || s === "ready_for_bank") return "bg-sky-50 text-sky-800 ring-1 ring-sky-200";
  if (s === "bank_generated") return "bg-teal-50 text-teal-800 ring-1 ring-teal-200";
  if (s === "payout_in_progress") return "bg-violet-50 text-violet-800 ring-1 ring-violet-200";
  if (s === "cancelled") return "bg-slate-100 text-slate-600 ring-1 ring-slate-200";
  return "bg-slate-50 text-slate-700 ring-1 ring-slate-200";
};

const formatMoney = (value) => {
  const n = Number(value ?? 0);
  return `₹ ${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
};

const PAGE_SIZE = 5;
const SUCCESS_VISIBLE_MS = 30000;
const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DISBURSEMENT_AUDIT = /salary_batch|bank_file|bank_response|payment_artifact|payout/i;

const AUDIT_LABELS = {
  "salary_batch.created": "Disbursement created",
  "salary_batch.updated": "Name updated",
  "salary_batch.deleted": "Disbursement deleted",
  "salary_batch.approved": "Approval recorded",
  "salary_batch.admin_approved": "Administrator approval",
  "salary_batch.override_approved": "Approval overridden",
  "salary_batch.rejected": "Rejected",
  "salary_batch.reopened": "Reopened",
  "salary_batch.cancelled": "Cancelled",
  "salary_batch.payout_enqueued": "Payout queued",
  "bank_file.downloaded": "Bank file downloaded",
  "bank_response.applied": "Bank response applied",
};

function disbursementNameFromSelection(opt) {
  if (!opt) return "";
  const period = opt.period || {};
  const run = opt.run || {};
  const sources = [
    period.start_date,
    period.period_start,
    run.period_start,
    run.start_date,
    opt.label,
    period.period_label,
  ];
  for (const src of sources) {
    const match = String(src || "").match(/(\d{4})-(\d{2})(?:-\d{2})?/);
    if (!match) continue;
    const month = Number(match[2]);
    if (month >= 1 && month <= 12) {
      return `${MONTHS[month - 1]}_${match[1]} Paydisbursement`.slice(0, 50);
    }
  }
  const text = String(opt.label || period.period_label || "");
  const monthIndex = MONTHS.findIndex((name) =>
    new RegExp(`\\b${name.slice(0, 3)}`, "i").test(text)
  );
  const year = text.match(/\b(20\d{2})\b/);
  if (monthIndex >= 0 && year) {
    return `${MONTHS[monthIndex]}_${year[1]} Paydisbursement`.slice(0, 50);
  }
  return "";
}

function auditActionLabel(action) {
  if (AUDIT_LABELS[action]) return AUDIT_LABELS[action];
  return String(action || "Event").replaceAll(".", " · ").replaceAll("_", " ");
}

function auditDetail(row) {
  const after = row?.details?.after || {};
  const before = row?.details?.before || {};
  const parts = [after.batch_ref, after.step, after.reason, after.comment].filter(Boolean);
  if (before.status && after.status && before.status !== after.status) {
    parts.push(`${before.status} → ${after.status}`);
  } else if (after.status) {
    parts.push(after.status);
  }
  if (parts.length) return parts.join(" · ");
  return row?.entity_id || "—";
}

function formatWhen(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function RecordPager({ page, total, onPage }) {
  if (!total) return null;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const start = (page - 1) * PAGE_SIZE + 1;
  const end = Math.min(total, page * PAGE_SIZE);
  const windowStart = Math.max(1, Math.min(page - 2, pages - 4));
  const windowEnd = Math.min(pages, windowStart + 4);
  const numbers = [];
  for (let n = windowStart; n <= windowEnd; n += 1) numbers.push(n);
  return (
    <div className="bank-pager">
      <span>
        Showing {start}–{end} of {total}
        {pages === 1 ? " · 5 per page" : ""}
      </span>
      {pages > 1 ? <div className="bank-pager-btns">
        <button type="button" disabled={page <= 1} onClick={() => onPage(page - 1)}>
          Prev
        </button>
        {numbers.map((n) => (
          <button
            key={n}
            type="button"
            className={n === page ? "is-on" : ""}
            onClick={() => onPage(n)}
          >
            {n}
          </button>
        ))}
        <button type="button" disabled={page >= pages} onClick={() => onPage(page + 1)}>
          Next
        </button>
      </div> : null}
    </div>
  );
}

function DecisionCard({
  tone,
  icon: Icon,
  kicker,
  title,
  batch,
  body,
  steps,
  reason,
  onReason,
  reasonLabel,
  confirmLabel,
  confirmClass,
  submitting,
  onClose,
  onConfirm,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-slate-900/50 p-4 backdrop-blur-[3px]">
      <div className={`bank-decision bank-decision--${tone}`} role="dialog" aria-modal="true">
        <div className="bank-decision-head">
          <div className="bank-decision-icon" aria-hidden>
            <Icon size={18} strokeWidth={2} />
          </div>
          <div className="min-w-0">
            <div className="bank-decision-kicker">{kicker}</div>
            <h2>{title}</h2>
          </div>
          <button
            type="button"
            className="bank-decision-close"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
          >
            <X size={16} />
          </button>
        </div>
        <div className="bank-decision-body">
          <div className="bank-decision-ref">{batch?.batch_ref || "—"}</div>
          <div className="bank-decision-run">{resolvePayrollRunDisplay(batch)}</div>
          <div className="bank-decision-stats">
            <div>
              <span>Employees</span>
              <strong>{batch?.total_employees ?? 0}</strong>
            </div>
            <div>
              <span>Amount</span>
              <strong>{formatMoney(batch?.total_amount)}</strong>
            </div>
            <div>
              <span>Status</span>
              <strong className="capitalize">{(batch?.status || "—").replaceAll("_", " ")}</strong>
            </div>
          </div>
          <p>{body}</p>
          {steps?.length ? (
            <ol className="bank-decision-steps">
              {steps.map((step) => (
                <li key={step.label} className={`is-${step.state}`}>
                  {step.label}
                </li>
              ))}
            </ol>
          ) : null}
          <label className="bank-decision-note">
            {reasonLabel}
            <textarea
              value={reason}
              onChange={(e) => onReason(e.target.value)}
              rows={3}
              placeholder="Saved with this decision"
              disabled={submitting}
            />
          </label>
        </div>
        <div className="bank-decision-foot">
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700"
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-sm font-medium text-white disabled:opacity-50 ${confirmClass}`}
            disabled={submitting}
          >
            {submitting ? <Spinner className="h-4 w-4" /> : null}
            {submitting ? "Working…" : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

const btnBase =
  "inline-flex items-center justify-center gap-1.5 rounded-lg px-2.5 py-1.5 text-xs font-medium transition disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap";

function ActionButton({ tone = "slate", onClick, disabled, children, title }) {
  const tones = {
    slate: "bg-slate-50 text-slate-700 ring-1 ring-slate-200 hover:bg-slate-100",
    blue: "bg-sky-50 text-sky-800 ring-1 ring-sky-200 hover:bg-sky-100",
    green: "bg-emerald-50 text-emerald-800 ring-1 ring-emerald-200 hover:bg-emerald-100",
    amber: "bg-amber-50 text-amber-900 ring-1 ring-amber-200 hover:bg-amber-100",
    rose: "bg-rose-50 text-rose-800 ring-1 ring-rose-200 hover:bg-rose-100",
    indigo: "bg-indigo-50 text-indigo-800 ring-1 ring-indigo-200 hover:bg-indigo-100",
    primary: "bg-blue-600 text-white shadow-sm hover:bg-blue-700",
  };
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`${btnBase} ${tones[tone] || tones.slate}`}
    >
      {children}
    </button>
  );
}

function Spinner({ className = "h-3.5 w-3.5" }) {
  return <Loader2 className={`${className} animate-spin`} strokeWidth={2} />;
}

export default function BankPayments() {
  const { role } = useContext(AuthContext);
  const isAdmin = useMemo(() => role === "admin", [role]);
  const canHrApprove = useMemo(() => ["admin", "hr"].includes(role), [role]);
  const canEditDelete = useMemo(() => ["admin", "hr"].includes(role), [role]);
  const canPayout = useMemo(() => ["admin", "finance"].includes(role), [role]);
  const canCancel = useMemo(() => ["admin", "hr", "finance"].includes(role), [role]);

  const [adminDialog, setAdminDialog] = useState(null);
  const [adminReason, setAdminReason] = useState("");
  const [hrDialog, setHrDialog] = useState(null);
  const [hrReason, setHrReason] = useState("");
  const [guideOpen, setGuideOpen] = useState(false);
  const [batchPage, setBatchPage] = useState(1);
  const [auditRows, setAuditRows] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditDenied, setAuditDenied] = useState(false);
  const [auditPage, setAuditPage] = useState(1);

  const [listLoading, setListLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [error, setError] = useState("");
  const [validationError, setValidationError] = useState(null);
  const [info, setInfo] = useState("");
  const [batches, setBatches] = useState([]);

  const [showModal, setShowModal] = useState(false);
  const [createData, setCreateData] = useState({
    payroll_run_id: "",
    pay_period_id: "",
    batch_ref: "",
  });
  const [selectedCreateRun, setSelectedCreateRun] = useState(null);

  const [preflight, setPreflight] = useState(null);
  const [bankDialog, setBankDialog] = useState(null);
  const [bankOptions, setBankOptions] = useState({ banks: [], formats: [] });
  const [bankOptionsLoading, setBankOptionsLoading] = useState(false);
  const [bankForm, setBankForm] = useState({ bankCode: "DEFAULT", exportFormat: "csv" });

  const [editDialog, setEditDialog] = useState(null);
  const [editRef, setEditRef] = useState("");
  const [deleteDialog, setDeleteDialog] = useState(null);
  const [menuOpen, setMenuOpen] = useState(null);
  const [menuPos, setMenuPos] = useState(null);
  const menuRef = useRef(null);
  const menuButtonRef = useRef(null);

  const rowBusy = (batchId) =>
    Boolean(actionLoading) && String(actionLoading).includes(String(batchId));
  const pageBusy = listLoading || actionLoading === "create-batch";

  const closeMenu = useCallback(() => {
    setMenuOpen(null);
    setMenuPos(null);
  }, []);

  const openRowMenu = useCallback((batchId, buttonEl) => {
    if (!buttonEl) return;
    const rect = buttonEl.getBoundingClientRect();
    const menuWidth = 208; // w-52
    const estimatedHeight = 160;
    const left = Math.min(
      Math.max(8, rect.right - menuWidth),
      window.innerWidth - menuWidth - 8
    );
    const openUp = rect.bottom + estimatedHeight > window.innerHeight - 8;
    const top = openUp
      ? Math.max(8, rect.top - estimatedHeight - 6)
      : Math.min(rect.bottom + 6, window.innerHeight - estimatedHeight - 8);
    setMenuPos({ top, left });
    setMenuOpen(batchId);
    menuButtonRef.current = buttonEl;
  }, []);

  useEffect(() => {
    if (!menuOpen) return undefined;
    const onDoc = (e) => {
      const inMenu = menuRef.current?.contains(e.target);
      const inBtn = menuButtonRef.current?.contains(e.target);
      if (!inMenu && !inBtn) closeMenu();
    };
    const onReposition = () => closeMenu();
    document.addEventListener("mousedown", onDoc);
    window.addEventListener("resize", onReposition);
    window.addEventListener("scroll", onReposition, true);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      window.removeEventListener("resize", onReposition);
      window.removeEventListener("scroll", onReposition, true);
    };
  }, [menuOpen, closeMenu]);

  const load = useCallback(async ({ silent } = {}) => {
    if (!silent) setListLoading(true);
    setError("");
    try {
      const res = await listSalaryBatches();
      setBatches(res.data || []);
    } catch (e) {
      setError(formatFinancialApiError(e, { context: "load salary batches" }));
    } finally {
      if (!silent) setListLoading(false);
    }
  }, []);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const data = await fetchOrgAuditLogs({ limit: 300 });
      const items = (data?.items || []).filter((row) =>
        DISBURSEMENT_AUDIT.test(`${row.action || ""} ${row.entity || ""}`)
      );
      setAuditRows(items);
      setAuditDenied(false);
    } catch (e) {
      setAuditRows([]);
      setAuditDenied(e?.response?.status === 403);
    } finally {
      setAuditLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    loadAudit();
  }, [load, loadAudit]);

  useEffect(() => {
    if (!info) return undefined;
    const timer = setTimeout(() => setInfo(""), SUCCESS_VISIBLE_MS);
    return () => clearTimeout(timer);
  }, [info]);

  useEffect(() => {
    const pages = Math.max(1, Math.ceil(batches.length / PAGE_SIZE));
    setBatchPage((current) => Math.min(current, pages));
  }, [batches.length]);

  useEffect(() => {
    const pages = Math.max(1, Math.ceil(auditRows.length / PAGE_SIZE));
    setAuditPage((current) => Math.min(current, pages));
  }, [auditRows.length]);

  const ensureBankOptions = useCallback(async () => {
    if (bankOptions.banks?.length) return bankOptions;
    setBankOptionsLoading(true);
    try {
      const { data } = await getBankFileOptions();
      const next = {
        banks: data?.banks || [],
        formats: data?.formats || [
          { id: "csv", label: "CSV" },
          { id: "excel", label: "Excel (XLSX)" },
          { id: "pdf", label: "PDF" },
        ],
      };
      setBankOptions(next);
      return next;
    } catch (e) {
      setError(formatFinancialApiError(e, { context: "load bank options" }));
      return { banks: [], formats: [] };
    } finally {
      setBankOptionsLoading(false);
    }
  }, [bankOptions]);

  const runAction = async (actionKey, fn, { successMessage, context, refresh = true } = {}) => {
    if (actionLoading) return false;
    setActionLoading(actionKey);
    setError("");
    setValidationError(null);
    setInfo("");
    try {
      await fn();
      if (successMessage) setInfo(successMessage);
      if (refresh) {
        await load({ silent: true });
        loadAudit();
      }
      return true;
    } catch (e) {
      const structured = parseBankFileValidationError(e);
      if (structured) {
        setValidationError(structured);
        setError("");
        return false;
      }
      const message = formatFinancialApiError(e, { context: context || "action" });
      const status = e?.response?.status;
      const isBenignConflict =
        status === 409 &&
        (String(message).toLowerCase().includes("already") ||
          String(message).toLowerCase().includes("refreshing"));
      if (isBenignConflict) {
        setInfo(message);
        if (refresh) await load({ silent: true });
        return true;
      }
      setError(message);
      return false;
    } finally {
      setActionLoading(null);
    }
  };

  const createBatch = () => {
    if (!createData.payroll_run_id || !createData.pay_period_id || !createData.batch_ref) {
      setError("Select a payroll run so the disbursement name can be filled in.");
      return;
    }
    runAction(
      "create-batch",
      async () => {
        await createSalaryBatch(createData);
        setShowModal(false);
        setCreateData({ payroll_run_id: "", pay_period_id: "", batch_ref: "" });
        setSelectedCreateRun(null);
      },
      {
        successMessage: `Disbursement created. ${createData.batch_ref} is ready for HR approval.`,
        context: "create disbursement",
      }
    );
  };

  const openHrDialog = (batch) => {
    closeMenu();
    setHrReason("");
    setHrDialog(batch);
  };

  const submitHrDialog = async () => {
    if (!hrDialog) return;
    const ok = await runAction(
      `hr:${hrDialog.batch_id}`,
      () =>
        approveSalaryBatchHr(hrDialog.batch_id, {
          comment: hrReason.trim() || "Approved by HR",
        }),
      {
        successMessage: `HR approval recorded for ${hrDialog.batch_ref}. You can generate the disbursement file now.`,
        context: "HR approval",
      }
    );
    if (ok) setHrDialog(null);
  };

  const openAdminDialog = (batch, mode) => {
    closeMenu();
    setAdminReason("");
    setAdminDialog({ batch, mode });
  };

  const submitAdminDialog = async () => {
    if (!adminDialog) return;
    const batchId = adminDialog.batch.batch_id;
    const { mode } = adminDialog;
    const body = { comment: adminReason || "" };
    const config = {
      approve: {
        fn: () => approveSalaryBatchAdmin(batchId, body, { override: false }),
        successMessage: "Payroll approved by Administrator.",
        context: "administrator approval",
      },
      override: {
        fn: () => approveSalaryBatchAdmin(batchId, body, { override: true }),
        successMessage: "Approval overridden by Administrator.",
        context: "administrator override",
      },
      reject: {
        fn: () => rejectSalaryBatch(batchId, body),
        successMessage: "Payroll rejected.",
        context: "reject payroll",
      },
      reopen: {
        fn: () => reopenSalaryBatch(batchId, body),
        successMessage: "Batch reopened for approval.",
        context: "reopen batch",
      },
    }[mode];
    const ok = await runAction(`admin-${mode}:${batchId}`, config.fn, {
      successMessage: config.successMessage,
      context: config.context,
    });
    if (ok) setAdminDialog(null);
  };

  const payout = (batchId) => {
    const ok = window.confirm(
      "API Payout sends money via RazorpayX and needs provider keys.\n\n" +
        "If you only need the bank upload file, click Cancel and use “Generate Disbursement” instead.\n\n" +
        "Continue with API Payout?"
    );
    if (!ok) return;
    runAction(
      `payout:${batchId}`,
      () => payoutSalaryBatch(batchId),
      { successMessage: "Payout queued.", context: "payout" }
    );
  };

  const openBankDialog = async (batch) => {
    closeMenu();
    if (!isApproved(batch.status)) {
      setError(
        "Bank file generation requires HR approval (or Administrator approval). Approve the batch first."
      );
      return;
    }
    setValidationError(null);
    setBankForm({
      bankCode: "",
      exportFormat: "excel",
    });
    setBankDialog(batch);
    await ensureBankOptions();
  };

  const submitBankFile = async ({ bankCode: codeOverride, exportFormat: formatOverride } = {}) => {
    if (!bankDialog) return;
    const batchId = bankDialog.batch_id;
    const bankCode = codeOverride || bankForm.bankCode;
    const exportFormat = formatOverride || bankForm.exportFormat;
    if (!bankCode) {
      setError("Select a bank before generating the upload file.");
      return;
    }
    setBankForm({ bankCode, exportFormat });
    const ok = await runAction(
      `bank-file:${batchId}`,
      async () => {
        const { data } = await generateSalaryBatchBankFile(batchId, {
          bankCode,
          exportFormat,
        });
        const ext =
          data?.format ||
          (exportFormat === "excel" || exportFormat === "xlsx" ? "xlsx" : exportFormat) ||
          "csv";
        const filename =
          data?.filename ||
          `${bankDialog.batch_ref || "salary"}_${bankCode}_bank_file.${ext}`;
        let blob = null;

        // Prefer inline bytes from generate response (most reliable).
        if (data?.content_base64) {
          blob = base64ToBlob(data.content_base64, mimeForBankExport(ext));
        } else {
          const downloadUrl =
            data?.download_url ||
            (data?.artifact_id
              ? `/api/disbursement/salary-batches/${batchId}/artifacts/${data.artifact_id}/download`
              : null);
          if (!downloadUrl) {
            throw new Error(
              "Bank file was generated but no download link was returned. Refresh and try again."
            );
          }
          const blobRes = await fetchArtifactBlob(downloadUrl);
          blob = blobRes?.data;
          // Axios may return a JSON error body as a Blob when responseType is blob.
          if (blob && typeof blob === "object" && blob.type && blob.type.includes("json")) {
            const text = await blob.text();
            let detail = "Bank file download failed.";
            try {
              const parsed = JSON.parse(text);
              detail =
                typeof parsed?.detail === "string"
                  ? parsed.detail
                  : parsed?.detail?.message || parsed?.message || detail;
            } catch {
              /* keep default */
            }
            throw new Error(detail);
          }
        }

        if (!blob || !(blob instanceof Blob) || blob.size === 0) {
          throw new Error("Bank file download was empty. Please try Generate & Download again.");
        }
        downloadBlob(blob, filename);

        const excluded = Number(data?.excluded_count || 0);
        const included = Number(data?.included_count || 0);
        if (excluded > 0) {
          setInfo(
            `Bank file downloaded for ${bankCode} (${included} row${included === 1 ? "" : "s"} · ${exportFormat.toUpperCase()}). ` +
              `${excluded} employee${excluded === 1 ? "" : "s"} still need bank account/IFSC in People Hub → Banking before you upload to the bank. ` +
              `Those rows are marked “Needs Bank Details” in the file.`
          );
        } else {
          setInfo(
            `Bank file downloaded for ${bankCode} (${exportFormat.toUpperCase()}). Upload it in your bank’s corporate portal.`
          );
        }
      },
      {
        context: "bank file generation",
      }
    );
    if (ok) setBankDialog(null);
  };

  const runPreflight = (batchId) => {
    closeMenu();
    return runAction(
      `preflight:${batchId}`,
      async () => {
        const { data } = await validateSalaryBatch(batchId);
        setPreflight({ batchId, ...data });
      },
      { context: "preflight validation", refresh: false }
    );
  };

  const cancelBatch = (batchId) => {
    closeMenu();
    if (
      !window.confirm(
        "Cancel this batch? This blocks bank file generation and payout. Successfully paid batches cannot be cancelled."
      )
    )
      return;
    runAction(
      `cancel:${batchId}`,
      () => cancelSalaryBatch(batchId),
      { successMessage: "Batch cancelled.", context: "batch cancel" }
    );
  };

  const openEdit = (batch) => {
    closeMenu();
    setEditRef(batch.batch_ref || "");
    setEditDialog(batch);
  };

  const submitEdit = async () => {
    if (!editDialog) return;
    const ref = (editRef || "").trim();
    if (ref.length < 3) {
      setError("Batch ref must be at least 3 characters");
      return;
    }
    const ok = await runAction(
      `edit:${editDialog.batch_id}`,
      () => updateSalaryBatch(editDialog.batch_id, { batch_ref: ref }),
      { successMessage: "Batch updated.", context: "edit batch" }
    );
    if (ok) setEditDialog(null);
  };

  const openDelete = (batch) => {
    closeMenu();
    setDeleteDialog(batch);
  };

  const submitDelete = async () => {
    if (!deleteDialog) return;
    const ok = await runAction(
      `delete:${deleteDialog.batch_id}`,
      () => deleteSalaryBatch(deleteDialog.batch_id),
      { successMessage: "Batch deleted.", context: "delete batch" }
    );
    if (ok) setDeleteDialog(null);
  };

  const primaryActions = (b) => {
    const busy = Boolean(actionLoading);
    const thisBusy = rowBusy(b.batch_id);
    const items = [];

    if (isApproved(b.status)) {
      items.push({
        key: "generate",
        label: actionLoading === `bank-file:${b.batch_id}` ? "Generating…" : "Generate Disbursement",
        tone: "primary",
        onClick: () => openBankDialog(b),
        loading: actionLoading === `bank-file:${b.batch_id}`,
        disabled: busy,
        title: "Generate bank upload file or open enterprise API disbursement",
      });
    }

    if (canHrApprove && ["hr_pending", "draft", "finance_pending"].includes(b.status)) {
      items.push({
        key: "hr",
        label: actionLoading === `hr:${b.batch_id}` ? "Approving…" : "HR Approve",
        tone: "amber",
        onClick: () => openHrDialog(b),
        loading: actionLoading === `hr:${b.batch_id}`,
        disabled: busy,
      });
    }

    if (canPayout && isApproved(b.status)) {
      items.push({
        key: "payout",
        label: actionLoading === `payout:${b.batch_id}` ? "Queuing…" : "API Payout",
        tone: "green",
        onClick: () => payout(b.batch_id),
        loading: actionLoading === `payout:${b.batch_id}`,
        disabled: busy,
        title: "Requires RazorpayX. Prefer Generate Disbursement for bank portal upload.",
      });
    }

    return { items, thisBusy };
  };

  const secondaryItems = (b) => {
    const busy = Boolean(actionLoading);
    const list = [
      {
        key: "preflight",
        label: actionLoading === `preflight:${b.batch_id}` ? "Checking…" : "Preflight check",
        onClick: () => runPreflight(b.batch_id),
        disabled: busy,
      },
    ];

    if (canEditDelete && EDITABLE_STATUSES.has(b.status)) {
      list.push({
        key: "edit",
        label: "Edit batch ref",
        icon: Pencil,
        onClick: () => openEdit(b),
        disabled: busy,
      });
    }

    if (canCancel && !["paid", "completed", "cancelled"].includes(b.status)) {
      list.push({
        key: "cancel",
        label: actionLoading === `cancel:${b.batch_id}` ? "Cancelling…" : "Cancel batch",
        danger: true,
        onClick: () => cancelBatch(b.batch_id),
        disabled: busy,
      });
    }

    if (canEditDelete && DELETABLE_STATUSES.has(b.status) && !["paid", "completed", "payout_in_progress"].includes(b.status)) {
      list.push({
        key: "delete",
        label: "Delete batch",
        icon: Trash2,
        danger: true,
        onClick: () => openDelete(b),
        disabled: busy,
      });
    }

    if (isAdmin && !["paid", "completed", "cancelled"].includes(b.status)) {
      if (b.status === "hr_pending" || b.status === "draft") {
        list.push({
          key: "admin-approve",
          label: "Approve as Admin",
          onClick: () => openAdminDialog(b, "approve"),
          disabled: busy,
        });
      }
      if (isApproved(b.status)) {
        list.push({
          key: "override",
          label: "Override approval",
          onClick: () => openAdminDialog(b, "override"),
          disabled: busy,
        });
      }
      if (b.status !== "rejected") {
        list.push({
          key: "reject",
          label: "Reject",
          danger: true,
          onClick: () => openAdminDialog(b, "reject"),
          disabled: busy,
        });
      }
      list.push({
        key: "reopen",
        label: "Reopen",
        onClick: () => openAdminDialog(b, "reopen"),
        disabled: busy,
      });
    }

    return list;
  };

  const pagedBatches = batches.slice((batchPage - 1) * PAGE_SIZE, batchPage * PAGE_SIZE);
  const pagedAudit = auditRows.slice((auditPage - 1) * PAGE_SIZE, auditPage * PAGE_SIZE);

  return (
    <BrixPageShell className="min-h-screen w-full max-w-full min-w-0 bg-gradient-to-br from-slate-50 via-white to-sky-50/40">
      <BrixPageHero
        className="bank-pay-hero"
        title="Payroll Disbursement"
        subtitle="Generate the bank upload file after payroll is approved, then pay from your bank portal."
        actions={
          <div className="flex flex-wrap gap-2">
            <BrixBtn
              variant="ghost"
              type="button"
              onClick={() => {
                load();
                loadAudit();
              }}
              disabled={pageBusy || Boolean(actionLoading)}
            >
              {listLoading ? <Spinner className="h-4 w-4" /> : <RefreshCw className="h-4 w-4" />}
              Refresh
            </BrixBtn>
            <BrixBtn
              type="button"
              className="bank-pay-live-btn"
              onClick={() => setShowModal(true)}
              disabled={pageBusy || Boolean(actionLoading)}
            >
              <span className="bank-pay-live-dot" aria-hidden />
              <Plus className="h-4 w-4" />
              Create Disbursement
            </BrixBtn>
          </div>
        }
      />

      <div className="mb-4">
        <button
          type="button"
          className="bank-pay-guide"
          aria-expanded={guideOpen}
          onClick={() => setGuideOpen((open) => !open)}
        >
          <Info className="h-4 w-4" aria-hidden />
          When to use this page
          <ChevronDown className={`h-4 w-4 transition ${guideOpen ? "rotate-180" : ""}`} />
        </button>
        {guideOpen ? (
          <ol className="bank-pay-guide-panel list-decimal">
            <li>Finish payroll processing and approval.</li>
            <li>Create a disbursement from that completed payroll run.</li>
            <li>
              Get HR approval, then click <span className="font-semibold">Generate Disbursement</span>,
              choose your bank, and download the bank-specific upload file.
            </li>
          </ol>
        ) : null}
      </div>

      {validationError ? (
        <div className="mb-4 overflow-hidden rounded-2xl border border-amber-200 bg-amber-50 shadow-sm">
          <div className="flex items-start justify-between gap-3 border-b border-amber-200/70 px-4 py-3">
            <div>
              <div className="text-sm font-semibold text-amber-950">
                {validationError.message}
              </div>
              {validationError.hint ? (
                <div className="mt-1 text-sm text-amber-900/90">{validationError.hint}</div>
              ) : null}
              {validationError.bankCode ? (
                <div className="mt-1 text-xs font-medium uppercase tracking-wide text-amber-800">
                  Bank: {validationError.bankCode}
                  {validationError.errorCount
                    ? ` · ${validationError.errorCount} issue(s)`
                    : ""}
                </div>
              ) : null}
            </div>
            <button
              type="button"
              onClick={() => setValidationError(null)}
              className="rounded-lg p-1.5 text-amber-800 hover:bg-amber-100"
              aria-label="Dismiss"
            >
              <X size={16} />
            </button>
          </div>
          {validationError.steps?.length ? (
            <div className="border-b border-amber-200/60 px-4 py-3 text-sm text-amber-950">
              <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-amber-800">
                How to resolve
              </div>
              <ol className="list-decimal space-y-1 pl-4">
                {validationError.steps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
            </div>
          ) : null}
          {validationError.issues?.length ? (
            <ul className="max-h-64 space-y-2 overflow-y-auto px-4 py-3">
              {validationError.issues.map((iss, i) => (
                <li
                  key={i}
                  className="rounded-xl border border-amber-200/80 bg-white/70 px-3 py-2 text-sm"
                >
                  <div className="font-medium text-slate-900">
                    {iss.employee_code || iss.employee_name || iss.employee_id} —{" "}
                    {iss.title || iss.detail}
                  </div>
                  {iss.how_to_fix ? (
                    <div className="mt-1 text-xs leading-relaxed text-slate-600">
                      {iss.how_to_fix}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          ) : validationError.errors?.length ? (
            <ul className="max-h-48 list-disc space-y-1 overflow-y-auto px-8 py-3 text-sm text-amber-950">
              {validationError.errors.map((e, i) => (
                <li key={i}>{e}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}

      {error ? (
        <div className="mb-4 whitespace-pre-wrap rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
          <div>{error}</div>
          {String(error).toLowerCase().includes("lifecycle") ? (
            <div className="mt-2 text-amber-950">
              Processed payroll must be approved before disbursement.{" "}
              <Link to="/payroll-finalize" className="font-semibold underline">
                Open Finalize Payroll
              </Link>{" "}
              (Verify → Approve), then create the batch again.
            </div>
          ) : null}
        </div>
      ) : null}

      {info ? (
        <div className="bank-pay-toast" key={info} role="status">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">{info}</span>
          <button
            type="button"
            onClick={() => setInfo("")}
            className="rounded-md p-1 text-emerald-800 hover:bg-emerald-100"
            aria-label="Dismiss"
          >
            <X size={14} />
          </button>
          <div className="bank-pay-toast-bar" />
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-100 bg-slate-50/80 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3.5">Batch Ref</th>
                <th className="px-5 py-3.5">Payroll Run</th>
                <th className="px-5 py-3.5">Employees</th>
                <th className="px-5 py-3.5">Total Amount</th>
                <th className="px-5 py-3.5">Status</th>
                <th className="px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100">
              {listLoading ? (
                <tr>
                  <td className="px-5 py-10 text-center text-slate-500" colSpan={6}>
                    <div className="inline-flex items-center gap-2">
                      <Spinner className="h-4 w-4" />
                      Loading batches…
                    </div>
                  </td>
                </tr>
              ) : batches.length ? (
                pagedBatches.map((b) => {
                  const { items } = primaryActions(b);
                  const more = secondaryItems(b);
                  const open = menuOpen === b.batch_id;
                  return (
                    <tr key={b.batch_id} className="hover:bg-slate-50/60">
                      <td className="px-5 py-4">
                        <div className="font-semibold text-slate-900">{b.batch_ref}</div>
                        <div className="mt-0.5 font-mono text-[11px] text-slate-400">
                          {String(b.batch_id).slice(0, 8)}…
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="text-slate-900">{resolvePayrollRunDisplay(b)}</div>
                        <div className="text-xs text-slate-500">{resolvePayPeriodDisplay(b)}</div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-medium text-slate-800">{b.total_employees ?? 0}</div>
                        {(b.total_employees ?? 0) === 0 && isApproved(b.status) ? (
                          <div className="mt-0.5 max-w-[11rem] text-[11px] leading-snug text-amber-700">
                            No payable lines yet — check employee bank accounts
                          </div>
                        ) : null}
                      </td>
                      <td className="px-5 py-4 font-medium tabular-nums text-slate-900">
                        {formatMoney(b.total_amount)}
                      </td>
                      <td className="px-5 py-4">
                        <span
                          className={`inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize ${statusPill(
                            b.status
                          )}`}
                        >
                          {b.status}
                        </span>
                      </td>
                      <td className="px-5 py-4">
                        <div className="flex flex-wrap items-center justify-end gap-1.5">
                          {items.map((a) => (
                            <ActionButton
                              key={a.key}
                              tone={a.tone}
                              onClick={a.onClick}
                              disabled={a.disabled}
                              title={a.title}
                            >
                              {a.loading ? <Spinner /> : null}
                              {a.label}
                            </ActionButton>
                          ))}

                          {more.length ? (
                            <button
                              type="button"
                              onClick={(e) => {
                                if (open) closeMenu();
                                else openRowMenu(b.batch_id, e.currentTarget);
                              }}
                              className={`${btnBase} bg-white text-slate-600 ring-1 ring-slate-200 hover:bg-slate-50`}
                              disabled={Boolean(actionLoading)}
                              title="More actions"
                              aria-label="More actions"
                              aria-expanded={open}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td className="px-5 py-14 text-center text-slate-500" colSpan={6}>
                    <div className="mx-auto flex max-w-md flex-col items-center gap-2">
                      <FileSpreadsheet className="h-8 w-8 text-slate-300" />
                      <div className="font-medium text-slate-700">No disbursements yet</div>
                      <div className="text-sm leading-relaxed">
                        After payroll is processed and approved, create a disbursement from that
                        completed run.
                      </div>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <RecordPager page={batchPage} total={batches.length} onPage={setBatchPage} />
      </div>

      <section className="mt-4 overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm">
        <div className="flex items-center justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
          <div className="flex items-center gap-2">
            <History className="h-4 w-4 text-slate-500" aria-hidden />
            <div>
              <h2 className="text-sm font-semibold text-slate-900">Disbursement audit</h2>
              <p className="text-xs text-slate-500">Existing records for this page, five at a time.</p>
            </div>
          </div>
          <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-semibold text-slate-600">
            {auditRows.length} records
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-slate-100 bg-slate-50/80 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-5 py-3">When</th>
                <th className="px-5 py-3">Action</th>
                <th className="px-5 py-3">Record</th>
                <th className="px-5 py-3">User</th>
                <th className="px-5 py-3">Detail</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {auditLoading ? (
                <tr>
                  <td className="px-5 py-8 text-center text-slate-500" colSpan={5}>
                    <span className="inline-flex items-center gap-2">
                      <Spinner className="h-4 w-4" />
                      Loading audit…
                    </span>
                  </td>
                </tr>
              ) : auditDenied ? (
                <tr>
                  <td className="px-5 py-8 text-center text-sm text-slate-500" colSpan={5}>
                    Audit history on this page is available to HR and administrators.
                  </td>
                </tr>
              ) : pagedAudit.length ? (
                pagedAudit.map((row) => (
                  <tr key={row.log_id} className="hover:bg-slate-50/60">
                    <td className="whitespace-nowrap px-5 py-3 text-slate-700">{formatWhen(row.occurred_at)}</td>
                    <td className="px-5 py-3 font-medium text-slate-900">{auditActionLabel(row.action)}</td>
                    <td className="px-5 py-3 font-mono text-xs text-slate-600">{row.entity_id || "—"}</td>
                    <td className="px-5 py-3 text-slate-700">{row.user || "System"}</td>
                    <td className="max-w-xs px-5 py-3 text-slate-600">{auditDetail(row)}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className="px-5 py-8 text-center text-sm text-slate-500" colSpan={5}>
                    No disbursement audit records yet. Creating or approving a disbursement writes a row here.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <RecordPager page={auditPage} total={auditRows.length} onPage={setAuditPage} />
      </section>

      {(() => {
        const openBatch = batches.find((x) => x.batch_id === menuOpen);
        if (!menuOpen || !menuPos || !openBatch) return null;
        const items = secondaryItems(openBatch);
        if (!items.length) return null;
        return createPortal(
          <div
            ref={menuRef}
            role="menu"
            className="fixed z-[80] w-52 rounded-xl border border-slate-200 bg-white py-1 shadow-xl"
            style={{ top: menuPos.top, left: menuPos.left }}
          >
            {items.map((m) => {
              const Icon = m.icon;
              return (
                <button
                  key={m.key}
                  type="button"
                  role="menuitem"
                  disabled={m.disabled}
                  onClick={m.onClick}
                  className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm disabled:opacity-50 ${
                    m.danger
                      ? "text-rose-700 hover:bg-rose-50"
                      : "text-slate-700 hover:bg-slate-50"
                  }`}
                >
                  {Icon ? <Icon className="h-3.5 w-3.5" /> : null}
                  {m.label}
                </button>
              );
            })}
          </div>,
          document.body
        );
      })()}

      {showModal ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/45 p-4 backdrop-blur-[3px]">
          <div className="bank-create" role="dialog" aria-modal="true" aria-labelledby="create-disbursement-title">
            <div className="bank-create-head">
              <div>
                <h2 id="create-disbursement-title">Create Disbursement</h2>
                <p>Choose a payroll run. The disbursement name fills in from that month.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-lg p-1.5 text-white hover:bg-white/10"
                disabled={Boolean(actionLoading)}
                aria-label="Close"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>

            <div className="bank-create-body space-y-4">
              <PayrollRunSelect
                value={createData.payroll_run_id}
                onChange={(runId, opt) => {
                  setCreateData({
                    ...createData,
                    payroll_run_id: runId,
                    pay_period_id: opt?.pay_period_id || "",
                    batch_ref: runId ? disbursementNameFromSelection(opt) : "",
                  });
                  setSelectedCreateRun(opt?.run || null);
                }}
                label="Payroll run"
                required
                disbursementOnly
                hint="Only processed payroll runs are listed."
                disabled={Boolean(actionLoading)}
              />
              <div>
                <label className="bank-create-label" htmlFor="disbursement-name">
                  Disbursement name
                </label>
                <input
                  id="disbursement-name"
                  type="text"
                  placeholder="October_2026 Paydisbursement"
                  value={createData.batch_ref}
                  onChange={(e) => setCreateData({ ...createData, batch_ref: e.target.value })}
                  className="bank-create-input"
                  maxLength={50}
                  disabled={Boolean(actionLoading)}
                />
                <p className="mt-1.5 text-xs text-slate-500">
                  Filled when you pick a run. Example: an October 2026 run becomes October_2026 Paydisbursement. You can edit it.
                </p>
              </div>
              {selectedCreateRun ? (
                <div className="bank-create-preview">
                  <div>
                    <span>Run status</span>
                    <strong>{resolveRunStatus(selectedCreateRun).label}</strong>
                  </div>
                  <div>
                    <span>Lifecycle</span>
                    <strong className="capitalize">
                      {(selectedCreateRun.lifecycle_status || "—").replaceAll("_", " ")}
                    </strong>
                  </div>
                </div>
              ) : null}
            </div>

            <div className="bank-create-foot">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-200"
                disabled={Boolean(actionLoading)}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={createBatch}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-800 disabled:opacity-50"
                disabled={Boolean(actionLoading) || !createData.batch_ref || !createData.payroll_run_id}
              >
                {actionLoading === "create-batch" ? <Spinner className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {actionLoading === "create-batch" ? "Creating…" : "Create Disbursement"}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Generate Disbursement — stepped enterprise dialog */}
      {bankDialog ? (
        <GenerateDisbursementDialog
          batch={bankDialog}
          banks={bankOptions.banks || []}
          formats={bankOptions.formats || []}
          loadingOptions={bankOptionsLoading}
          submitting={actionLoading === `bank-file:${bankDialog.batch_id}`}
          validationError={validationError}
          onClose={() => {
            if (actionLoading === `bank-file:${bankDialog.batch_id}`) return;
            setBankDialog(null);
          }}
          onGenerate={submitBankFile}
        />
      ) : null}

      {/* Edit */}
      {editDialog ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-start justify-between gap-4">
              <h2 className="text-lg font-semibold text-slate-900">Edit Batch</h2>
              <button
                type="button"
                onClick={() => setEditDialog(null)}
                className="rounded-lg p-1.5 hover:bg-slate-100"
                disabled={Boolean(actionLoading)}
              >
                <X size={18} />
              </button>
            </div>
            <p className="mt-1 text-sm text-slate-600">Update the batch reference label.</p>
            <input
              type="text"
              value={editRef}
              onChange={(e) => setEditRef(e.target.value)}
              className="mt-4 w-full rounded-xl border border-slate-200 px-3 py-2.5 outline-none focus:ring-2 focus:ring-sky-200"
              disabled={Boolean(actionLoading)}
            />
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setEditDialog(null)}
                className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium"
                disabled={Boolean(actionLoading)}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={submitEdit}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
                disabled={Boolean(actionLoading)}
              >
                {actionLoading === `edit:${editDialog.batch_id}` ? <Spinner className="h-4 w-4" /> : null}
                Save
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Delete */}
      {deleteDialog ? (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h2 className="text-lg font-semibold text-rose-800">Delete Batch</h2>
            <p className="mt-2 text-sm text-slate-700">
              Permanently delete{" "}
              <span className="font-mono font-semibold">{deleteDialog.batch_ref}</span>? This cannot
              be undone. Paid batches cannot be deleted.
            </p>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setDeleteDialog(null)}
                className="rounded-xl bg-slate-100 px-4 py-2.5 text-sm font-medium"
                disabled={Boolean(actionLoading)}
              >
                Keep
              </button>
              <button
                type="button"
                onClick={submitDelete}
                className="inline-flex items-center gap-2 rounded-xl bg-rose-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
                disabled={Boolean(actionLoading)}
              >
                {actionLoading === `delete:${deleteDialog.batch_id}` ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
                Delete
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {preflight ? (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-slate-900/40 p-4 backdrop-blur-[2px]">
          <div className="max-h-[85vh] w-full max-w-2xl overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900">Preflight Result</h2>
              <button
                type="button"
                onClick={() => setPreflight(null)}
                className="rounded-lg p-1.5 hover:bg-slate-100"
              >
                <X size={18} strokeWidth={2} />
              </button>
            </div>
            <div className="mt-4 grid grid-cols-2 gap-3 text-sm">
              <div>
                <div className="text-xs text-slate-500">Batch status</div>
                <div className="font-mono">{preflight.batch_status}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Bank</div>
                <div className="font-mono">{preflight.bank_code || "—"}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Payable items</div>
                <div className="font-mono text-emerald-700">{preflight.payable_items}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Rejected items</div>
                <div className="font-mono text-rose-700">{preflight.rejected_items}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Total amount</div>
                <div className="font-mono">{formatMoney(preflight.total_amount)}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Ready</div>
                <div
                  className={`font-mono ${preflight.ready ? "text-emerald-700" : "text-amber-700"}`}
                >
                  {preflight.ready ? "yes" : "no"}
                </div>
              </div>
            </div>
            {preflight.errors && preflight.errors.length > 0 ? (
              <div className="mt-4">
                <div className="mb-1 text-xs text-slate-500">
                  Issues ({preflight.error_count} total, showing first {preflight.errors.length})
                </div>
                <ul className="max-h-64 space-y-1 overflow-y-auto rounded-xl border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                  {preflight.errors.map((e, i) => (
                    <li key={i}>{e}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div className="mt-4 text-sm text-emerald-700">All payable rows validated cleanly.</div>
            )}
            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setPreflight(null)}
                className="rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-blue-700"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {hrDialog ? (
        <DecisionCard
          tone="hr"
          icon={ShieldCheck}
          kicker="Human resources"
          title="HR Approval"
          batch={hrDialog}
          body="Approving here records the HR decision and unlocks Generate Disbursement. The bank file is not sent until you generate it."
          steps={[
            { label: "Payroll done", state: "done" },
            { label: "HR approval", state: "current" },
            { label: "Generate file", state: "next" },
            { label: "Bank portal", state: "next" },
          ]}
          reason={hrReason}
          onReason={setHrReason}
          reasonLabel="Note for the audit log"
          confirmLabel="Approve as HR"
          confirmClass="bg-amber-600 hover:bg-amber-700"
          submitting={actionLoading === `hr:${hrDialog.batch_id}`}
          onClose={() => setHrDialog(null)}
          onConfirm={submitHrDialog}
        />
      ) : null}

      {adminDialog ? (
        (() => {
          const copy = {
            approve: {
              tone: "approve",
              icon: BadgeCheck,
              kicker: "Administrator",
              title: "Administrator Approval",
              body: "This approves the disbursement with administrator privileges and skips the waiting HR step. The decision is written to the audit log.",
              confirm: "Approve disbursement",
              confirmClass: "bg-blue-700 hover:bg-blue-800",
              steps: [
                { label: "Payroll done", state: "done" },
                { label: "Admin approval", state: "current" },
                { label: "Ready to pay", state: "next" },
              ],
            },
            override: {
              tone: "override",
              icon: ShieldAlert,
              kicker: "Administrator",
              title: "Override Approval",
              body: "This replaces the current approval state. Use it when a recorded decision must be corrected. The previous and new state stay in the audit log.",
              confirm: "Override approval",
              confirmClass: "bg-indigo-600 hover:bg-indigo-700",
              steps: [
                { label: "Recorded", state: "done" },
                { label: "Override", state: "current" },
                { label: "Audit", state: "next" },
              ],
            },
            reject: {
              tone: "reject",
              icon: Ban,
              kicker: "Administrator",
              title: "Reject Disbursement",
              body: "Rejecting blocks bank file generation and payout until someone reopens the disbursement.",
              confirm: "Reject disbursement",
              confirmClass: "bg-rose-600 hover:bg-rose-700",
              steps: [
                { label: "In review", state: "done" },
                { label: "Reject", state: "current" },
                { label: "Blocked", state: "next" },
              ],
            },
            reopen: {
              tone: "reopen",
              icon: RotateCcw,
              kicker: "Administrator",
              title: "Reopen Disbursement",
              body: "Reopening clears the current decision and sends the disbursement back to HR approval.",
              confirm: "Reopen",
              confirmClass: "bg-amber-600 hover:bg-amber-700",
              steps: [
                { label: "Closed", state: "done" },
                { label: "Reopen", state: "current" },
                { label: "HR approval", state: "next" },
              ],
            },
          }[adminDialog.mode];
          const submitting =
            actionLoading === `admin-${adminDialog.mode}:${adminDialog.batch?.batch_id}`;
          return (
            <DecisionCard
              tone={copy.tone}
              icon={copy.icon}
              kicker={copy.kicker}
              title={copy.title}
              batch={adminDialog.batch}
              body={copy.body}
              steps={copy.steps}
              reason={adminReason}
              onReason={setAdminReason}
              reasonLabel="Message / reason"
              confirmLabel={copy.confirm}
              confirmClass={copy.confirmClass}
              submitting={submitting}
              onClose={() => setAdminDialog(null)}
              onConfirm={submitAdminDialog}
            />
          );
        })()
      ) : null}
    </BrixPageShell>
  );
}
